import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Button,
    Typography,
    Stack,
    Dialog,
    DialogContent,
    Box,
    Slide,
    Grid,
    IconButton,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import { useQuery } from '@tanstack/react-query';
import { container } from '@infrastructure/container';
import DialogHeader from '@shared/ui/dialog/DialogHeader';
import { Close } from '@mui/icons-material';
import MarketplaceConnectorButton from '@features/connections/generic/components/MarketplaceConnectorButton';
import BolWarehouseForm from './bol/BolWarehouseForm';
import MontaWarehouseForm from './monta/MontaWarehouseForm';
import RequestConnectorForm from './request-connector/RequestConnectorForm';
import ManualWarehouseForm from './manual/ManualWarehouseForm';
import FileFlow from './file/FileFlow';
import { APP_CONFIG } from '../../../../config/app.config';
import { WarehouseConnectionType } from '@domain/inventory/enums/WarehouseConnectionType.enum';
import { ClientType } from '@domain/connection/models/Connector';
import Import4YouWarehouseForm from './import4you/Import4YouWarehouseForm';
import ProductFlowWarehouseForm from './product_flow/ProductFlowWarehouseForm';
import WooCommerceWarehouseForm from './woocommerce/WooCommerceWarehouseForm';
import ShopifyWarehouseForm from './shopify/ShopifyWarehouseForm';
import AmazonWarehouseForm from './amazon/AmazonWarehouseForm';
import ChannelDockWarehouseForm from './channeldock/ChannelDockWarehouseForm';

type CreateWarehouseStep = 'main' | 'bol' | 'monta' | 'import4you' | 'file' | 'manual' | 'product_flow' | 'woocommerce' | 'request-connector' | 'shopify' | 'amazon' | 'channeldock';

interface CreateWarehouseBottomSheetProps {
    open: boolean;
    onClose: () => void;
    /** When false, skip URL sync so nested opens (e.g. onboarding) don't open a second sheet. */
    syncUrl?: boolean;
}

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement;
    },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const SlideLeftTransition = React.forwardRef(function SlideLeftTransition(
    props: TransitionProps & {
        children: React.ReactElement;
    },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="left" ref={ref} {...props} />;
});

const CreateWarehouseBottomSheet: React.FC<CreateWarehouseBottomSheetProps> = ({
    open,
    onClose,
    syncUrl = true,
}) => {
    const [searchParams, setSearchParams] = useSearchParams();

    // State for managing which form is currently open
    const [currentForm, setCurrentForm] = useState<CreateWarehouseStep>('main');

    // Sync URL with current form state
    useEffect(() => {
        if (!syncUrl || !open) return;

        const newSearchParams = new URLSearchParams(searchParams);
        if (currentForm === 'main') {
            newSearchParams.set('create', 'warehouse');
            newSearchParams.delete('step');
        } else {
            newSearchParams.set('create', 'warehouse');
            newSearchParams.set('step', currentForm);
        }
        setSearchParams(newSearchParams);
    }, [open, currentForm, searchParams, setSearchParams, syncUrl]);

    // Reset to main when opening without URL sync
    useEffect(() => {
        if (open && !syncUrl) {
            setCurrentForm('main');
        }
    }, [open, syncUrl]);

    // Initialize form from URL when dialog opens
    useEffect(() => {
        if (!open || !syncUrl) return;

        const step = searchParams.get('step');
        if (step && ['bol', 'monta', 'import4you', 'product_flow', 'woocommerce', 'file', 'manual', 'request-connector', 'shopify', 'amazon', 'channeldock'].includes(step)) {
            setCurrentForm(step as CreateWarehouseStep);
        } else {
            setCurrentForm('main');
        }
    }, [open, searchParams, syncUrl]);

    const { data: connectors } = useQuery({
        queryKey: ['warehouse-connectors'],
        queryFn: async () => {
            const result = await container.marketplaceRepository.fetchConnectors('wms');
            if (result.status === 'error') throw new Error(result.error.message);
            return result.data;
        },
    });

    const handleClose = () => {
        setCurrentForm('main');
        if (syncUrl) {
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.delete('create');
            newSearchParams.delete('step');
            setSearchParams(newSearchParams);
        }
        onClose();
    };

    const handleBackToMain = () => {
        setCurrentForm('main');
    };

    const forms = [
        {
            key: 'bol',
            component: <BolWarehouseForm
                onClose={handleClose}
                onBack={handleBackToMain}
            />,
        },
        {
            key: 'monta',
            component: <MontaWarehouseForm
                onClose={handleClose}
                onBack={handleBackToMain}
            />,
        },
        {
            key: 'file',
            component: <FileFlow
                onClose={handleClose}
                onBack={handleBackToMain}
            />,
        },
        {
            key: 'manual',
            component: <ManualWarehouseForm
                onClose={handleClose}
                onBack={handleBackToMain}
            />,
        },
        {
            key: 'request-connector',
            component: <RequestConnectorForm
                onClose={handleClose}
                onBack={handleBackToMain}
            />
        },
        {
            key: 'import4you',
            component: <Import4YouWarehouseForm
                onClose={handleClose}
                onBack={handleBackToMain}
            />
        },
        {
            key: 'product_flow',
            component: <ProductFlowWarehouseForm
                onClose={handleClose}
                onBack={handleBackToMain}
            />
        },
        {
            key: 'woocommerce',
            component: <WooCommerceWarehouseForm
                onClose={handleClose}
                onBack={handleBackToMain}
            />
        },
        {
            key: 'shopify',
            component: <ShopifyWarehouseForm
                onClose={handleClose}
                onBack={handleBackToMain}
            />
        },
        {
            key: 'amazon',
            component: <AmazonWarehouseForm
                onClose={handleClose}
                onBack={handleBackToMain}
            />
        },
        {
            key: 'channeldock',
            component: <ChannelDockWarehouseForm
                onClose={handleClose}
                onBack={handleBackToMain}
            />
        }
    ]

    const handleConnectorClick = (connectorType: ClientType) => {
        switch (connectorType) {
            case ClientType.BOL:
                handleWarehouseConnectionTypeClick(WarehouseConnectionType.BOL);
                break;
            case ClientType.MONTA:
                handleWarehouseConnectionTypeClick(WarehouseConnectionType.MONTA);
                break;
            case ClientType.IMPORT4YOU:
                setCurrentForm('import4you');
                break;
            case ClientType.PRODUCT_FLOW:  
                setCurrentForm('product_flow');
                break;
            case ClientType.WOOCOMMERCE:
                setCurrentForm('woocommerce');
                break;
            case ClientType.SHOPIFY:
                setCurrentForm('shopify');
                break;
            case ClientType.AMAZON:
                setCurrentForm('amazon');
                break;
            case ClientType.CHANNELDOCK:
                setCurrentForm('channeldock');
                break;
            default:
                break;
        }
    }

    const handleWarehouseConnectionTypeClick = (connectorType: WarehouseConnectionType) => {
        switch (connectorType) {
            case WarehouseConnectionType.BOL:
                setCurrentForm('bol');
                break;
            case WarehouseConnectionType.MONTA:
                setCurrentForm('monta');
                break;
            case WarehouseConnectionType.IMPORT4YOU:
                setCurrentForm('import4you');
                break;
            case WarehouseConnectionType.FILE:
                setCurrentForm('file');
                break;
            case WarehouseConnectionType.MANUAL:
                setCurrentForm('manual');
                break;
            case WarehouseConnectionType.PRODUCT_FLOW:
                setCurrentForm('product_flow');
                break;
            case WarehouseConnectionType.WOOCOMMERCE:
                setCurrentForm('woocommerce');
                break;
            case WarehouseConnectionType.AMAZON:
                setCurrentForm('amazon');
                break;
            case WarehouseConnectionType.CHANNELDOCK:
                setCurrentForm('channeldock');
                break;
            case WarehouseConnectionType.REQUEST_CONNECTOR:
                setCurrentForm('request-connector');
                break;
        }
    };

    return (
        <>

            <Dialog
                open={open}
                onClose={handleClose}
                TransitionComponent={Transition}
                fullScreen
                sx={{
                    '& .MuiDialog-paper': {
                        margin: 0,
                        borderRadius: '8px 8px 0 0',
                        maxHeight: '90vh',
                        maxWidth: '800px',
                        minHeight: '60vh',
                        alignSelf: 'flex-end',
                    }
                }}
            >
                {/* Main form */}
                {currentForm === 'main' && (
                    <>

                        <DialogHeader>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography variant="h6" component="div">
                                    Create your Warehouse
                                </Typography>

                                <IconButton onClick={handleClose}>
                                    <Close />
                                </IconButton>
                            </Stack>
                        </DialogHeader>

                        <DialogContent sx={{ flex: 1, overflow: 'auto' }}>

                            <Stack gap={3}>
                                <Box>
                                    <Typography variant="h6" color="text.primary">
                                        Connect your inventory
                                    </Typography>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Create a custom warehouse set it up yourself or connect a warehouse to read the inventory automatically.</Typography>
                                </Box>


                                <Grid container spacing={3}>
                                    {connectors && connectors.map((connector) => (
                                        <Grid item xs={3} key={connector.type}>
                                            <MarketplaceConnectorButton
                                                connector={connector}
                                                onClick={() => handleConnectorClick(connector.type)}
                                            />
                                        </Grid>
                                    ))}

                                    <Grid item xs={3} key='file'>
                                        <Button
                                            variant="outlined"
                                            sx={{
                                                p: 1,
                                                width: '100%',
                                                height: '100%',
                                                borderColor: 'grey.300',
                                                '&:hover': {
                                                    borderColor: 'grey.400',
                                                },
                                            }}
                                            onClick={() => handleWarehouseConnectionTypeClick(WarehouseConnectionType.FILE)}
                                        >

                                            <img
                                                src={'https://fra.cloud.appwrite.io/v1/storage/buckets/66df29f1002e1bc53bcb/files/687e07b000058ea6dc02/view?project=66548b010010dccec4ec&mode=admin'}
                                                alt={'csv'}
                                                style={{ width: '100%', height: 'auto' }}
                                                onError={(e) => {
                                                    e.currentTarget.src = '/assets/images/ic_file.svg';
                                                }}
                                            />


                                        </Button>
                                    </Grid>

                                    <Grid item xs={3} key='more-connectors'>
                                        <Button
                                            variant="outlined"
                                            sx={{
                                                p: 1,
                                                borderColor: APP_CONFIG.ui.theme.colors.border,
                                                width: '100%',
                                                height: '100%',
                                            }}
                                            onClick={() => handleWarehouseConnectionTypeClick(WarehouseConnectionType.REQUEST_CONNECTOR)}
                                        >
                                            <Typography variant="body2" color="text.secondary">
                                                Show more..
                                            </Typography>
                                        </Button>
                                    </Grid>
                                </Grid>

                                <Button
                                    variant="outlined"
                                    sx={{
                                        p: 3,
                                        width: '100%',
                                        borderColor: 'grey.300',
                                        '&:hover': {
                                            borderColor: 'grey.400',
                                        },
                                    }}
                                    onClick={() => handleWarehouseConnectionTypeClick(WarehouseConnectionType.MANUAL)}
                                >
                                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} width="100%">
                                        <Stack alignItems="flex-start" >
                                            <Typography variant="subtitle2" color="text.primary">
                                                Nothing to connect to?
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                You can always create a warehouse and set it up manually
                                            </Typography>
                                        </Stack>

                                        <Box
                                            sx={{
                                                borderLeft: '1px solid',
                                                borderColor: 'divider',
                                                pl: 3,
                                                display: 'flex',
                                                alignItems: 'center',
                                                alignSelf: 'stretch'
                                            }}
                                        >
                                            <Typography variant="subtitle2" color="primary">
                                                Create manual warehouse
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Button>

                            </Stack>
                        </DialogContent>

                    </>

                )}

                {forms.map((form) => (
                    <SlideLeftTransition key={form.key} in={currentForm === form.key} mountOnEnter unmountOnExit>
                        <Box position="absolute" top={0} left={0} right={0} bottom={0} bgcolor="white" zIndex={1000}>
                            {form.component}
                        </Box>
                    </SlideLeftTransition>
                ))}
            </Dialog >

        </>
    );
};

export default CreateWarehouseBottomSheet; 